<template>
    <div>
        <div v-if="loaded">
            <div v-if="isShowSeatsMap">
                <div class="text-h6 text--secondary pb-2 ml-5">
                    {{ $t('seat_map') }}
                    <p
                        v-if="tourists?.length && selectedSeats?.length > tourists?.length"
                        class="text-subtitle-2 pt-1 text-red-darken-4 font-weight-regular"
                        v-html="$t('seat_selection_validation_error', {prop: tourists.length})"
                    />
                </div>
                <v-expansion-panels v-model="opennedPanels" variant="accordion" class="mb-5">
                    <v-expansion-panel>
                        <v-expansion-panel-title v-slot="{expanded}">
                            <p v-if="seatSelectionService?.price?.amount === 0">{{ $t('seat_selection_free') }}</p>
                            <p
                                v-else
                                v-html="
                                    $t('seat_selection_billable', {
                                        prop: `${seatSelectionService?.price?.amount} ${seatSelectionService?.price?.currency}`,
                                    })
                                "
                            />
                            <div class="d-flex flex-column justify-center align-center mr-4 ml-2">
                                <template v-if="selectedSeats.length">
                                    <v-chip
                                        v-for="(seat, index) in selectedSeats"
                                        :key="`${seat.rowIndex}-${seat.seatNumber}`"
                                        size="small"
                                        :disabled="disabled"
                                        label
                                        class="ma-1 text-capitalize"
                                        color="info"
                                        closable
                                        @click:close="unselectSeat(seat)"
                                    >
                                        <span v-if="tourists[index]?.prefix && tourists[index]?.lastName">
                                            {{ tourists[index]?.prefix || '' }}
                                            {{ tourists[index]?.lastName }}:
                                        </span>
                                        <span v-else>{{ $t('passenger', 1) }} {{ index + 1 }}:</span>
                                        <span class="ms-1">
                                            {{ seat.seatNumber }}
                                            <span v-if="seatSelectionService?.price?.amount">
                                                (+{{ $filters.price(seatSelectionService?.price) }})
                                            </span>
                                        </span>
                                    </v-chip>
                                </template>
                                <span v-else-if="expanded">
                                    {{ $t('flights.select_seat') }}
                                </span>
                            </div>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-item-group v-if="loaded" v-model="selected" multiple :max="selectableSeatsCount">
                                <SeatMap
                                    :seat-map="seats"
                                    :is-booking-mode="true"
                                    :disabled="disabled"
                                    @change="updateSeats"
                                />
                            </v-item-group>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>
            </div>
        </div>
        <div v-else>
            <span class="text-h6 text--secondary pb-2 ml-5">
                {{ $t('loadind_seats') }}
            </span>
            <v-skeleton-loader :type="`list-item@${1}`" :elevation="0" class="mb-5" />
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Emit, Prop, VModel, Vue, Watch} from 'vue-facing-decorator'
    import {isEqual} from '@/utils/helpers'
    import SeatMap from '~/components/SeatMap.vue'

    @Component({
        components: {SeatMap},
    })
    export class TransferSeatsMap extends Vue {
        @VModel() storeSelectedSeats
        @Prop({required: true}) storeIndex
        @Prop({required: true}) prepareBookRequest
        @Prop({required: true}) offer
        @Prop({required: true}) searchRequest
        @Prop({default: false, type: Boolean}) disabled
        @Prop({required: true}) tourists
        @Prop({required: true}) options
        @Prop() returnTransferOffer

        selected = {}
        loaded = false
        segmentRphs = []
        opennedPanels = false
        seatSelectionService = {}
        flightSeatsMapData = {}
        seats = {}
        selectedSeats = []

        @Watch('disabled')
        closeAllExpandPanels(n) {
            if (n) {
                this.opennedPanels = false
            }
        }

        updateSeats(seat, seatMap) {
            this.seats = seatMap
            if (!seat.available) {
                this.selectedSeats.push(seat)
            } else {
                this.selectedSeats = this.selectedSeats.filter(
                    ({rowIndex, seatNumber}) => seat.rowIndex !== rowIndex && seat.seatNumber !== seatNumber
                )
            }
            this.updatePrepareBookRequest()
            if (this.prepareBookRequest?.offerKey !== this.offer.offerKey) {
                this.prepareBookRequest.offerKey = this.offer.offerKey
            }
        }

        unselectSeat(seat) {
            this.selectedSeats = this.selectedSeats.filter(seatItem => !isEqual(seatItem, seat))
            this.seats[seat.rowIndex][seat.seatIndex].available = !this.seats[seat.rowIndex][seat.seatIndex].available
            this.updatePrepareBookRequest()
        }

        updatePrepareBookRequest() {
            this.storeSelectedSeats[this.storeIndex] = this.selectedSeats
        }

        async mounted() {
            this.seatSelectionService = this.options.seatSelectionServices[0]
            const vehicleTypeId = this.seatSelectionService?.vehicleTypeId
            if (vehicleTypeId) {
                await this.loadTransferSeatsMap(vehicleTypeId)
                if (
                    this.storeSelectedSeats[this.storeIndex]?.length &&
                    this.prepareBookRequest.offerKey === this.offer.offerKey
                ) {
                    this.selectedSeats = this.storeSelectedSeats[this.storeIndex]
                    this.storeSelectedSeats[1] = []
                } else {
                    if (!this.returnTransferOffer) this.storeSelectedSeats = [[], []]
                }
                this.seats = this.prepareSeatMap
            }
            this.loaded = true
        }

        async loadTransferSeatsMap(vehicleTypeId) {
            try {
                const {
                    seatMaps: [_seatMaps],
                } = await this.$api.seatMap.get({linkedObjectId: vehicleTypeId})

                if (_seatMaps && _seatMaps?.schema) {
                    this.flightSeatsMapData = JSON.parse(_seatMaps.schema)
                }
            } catch (e) {
                this.flightSeatsMapData = {}
                this.$api.$toast.error(this.$t('error_message.load_error'))
            }
        }

        get prepareSeatMap() {
            const newBusLayout = {}
            for (const [row, seats] of Object.entries(this.flightSeatsMapData)) {
                newBusLayout[row] = seats?.map((seat, seatIndex) => {
                    if (seat !== null) {
                        return {
                            seatNumber: seat,
                            rowIndex: row,
                            seatIndex,
                            occupied: false,
                            available: true,
                            orderId: null,
                            firstName: null,
                            lastName: null,
                        }
                    }
                    return null
                })
            }

            this.selectedSeats.forEach(selectedSeat => {
                for (const row in newBusLayout) {
                    newBusLayout[row].forEach(seat => {
                        if (seat !== null) {
                            if (
                                seat.seatNumber === selectedSeat.seatNumber &&
                                seat.rowIndex === selectedSeat.rowIndex
                            ) {
                                seat.available = false
                            }
                        }
                    })
                }
            })
            // TODO[p4] - add unavailable seats from the api
            return newBusLayout
        }

        get selectableSeatsCount() {
            return this.tourists.length
        }

        get isShowSeatsMap() {
            return !!Object.values(this.flightSeatsMapData)?.length
        }
    }

    export default TransferSeatsMap
</script>

<style scoped lang="scss">
    .loader {
        width: 200px;
    }
</style>
